import { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { snackbarTypes } from '@/constants/alertTypes';
import { selectExperimentKeys } from '@/reducers/ui/experimentsUiReducer';
import { getSelectedProjectId } from '@/reducers/ui/projectsUiReducer';
import alertsUtil from '@/util/alertsUtil';
import { useCurrentUserContext } from '@shared/hooks/useCurrentUserContext';
import { expDetailsTemplateKeys } from '@experiment-details/hooks/api/exp-details-template-key-factory';
import { loadExpDetailsTemplate } from '@experiment-details/hooks/api/expDetailsTemplateApi';
import useExpDetailsSelectedTemplate from '@experiment-details/hooks/useExpDetailsSelectedTemplate';
import { ExperimentTemplateData } from '@experiment-details/types';
import { useIsUserLoggedIn } from '@shared/hooks';

const useExpDetailsCheckViewsUpdates = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const {
    templateData: currentTemplate,
    isLoading
  } = useExpDetailsSelectedTemplate();
  const isUserLoggedIn = useIsUserLoggedIn();
  const { user: currentUser } = useCurrentUserContext();
  const projectId = useSelector(getSelectedProjectId);
  const [experimentKey] = useSelector(selectExperimentKeys);
  const currentViewId = currentTemplate?.templateId;

  const isEnabled = Boolean(
    experimentKey &&
      isUserLoggedIn &&
      currentTemplate &&
      !currentTemplate.personalView &&
      !isLoading
  );

  const selectUpdatedByAnotherUserView = (
    templateData: ExperimentTemplateData
  ) => {
    if (
      !templateData.personalView &&
      templateData.templateId === currentViewId &&
      templateData.updatedBy !== null &&
      templateData.updatedBy !== currentUser?.userName
    ) {
      return templateData;
    }
  };

  const { data: updatedView } = useQuery(
    ['projectTemplate', projectId, experimentKey, currentViewId],
    () =>
      loadExpDetailsTemplate({
        templateId: currentViewId as string,
        projectId: projectId
      }),
    {
      refetchInterval: 10_000, // Refetch every 10 seconds
      select: selectUpdatedByAnotherUserView,
      notifyOnChangeProps: ['data'],
      enabled: isEnabled,
      refetchOnMount: false
    }
  );

  useEffect(() => {
    if (!updatedView || !currentTemplate) return;

    if (currentTemplate?.isLocalTemplate) {
      dispatch(
        alertsUtil.openSnackbarDialog(
          snackbarTypes.SUCCESS_API_RESPONSE,
          `Your changes can’t be applied. The view has been modified by somebody else.`
        )
      );
    }

    queryClient.invalidateQueries(
      expDetailsTemplateKeys.projectTemplate(currentTemplate.templateId)
    );
  }, [updatedView]);
};

export default useExpDetailsCheckViewsUpdates;
